'use strict';

import {getVueTemplate, hasDataPageAttribute, debouncer, getResourceUrlForMarketoDetailIdUrl} from '../../../_imports/utils/utils.common';
import { decodeHtml } from '../../../_imports/shared/vue-utils.common';
import { isEqual } from 'lodash';
import BaseVueComponent from '../../../_imports/shared/base-vue-component.vue.common';
import DynamicResourceComponent from '../../shared/dynamic-resource.common';
import ResourceCard from '../resource-cards/resource-card.vue';
import CallToAction from '../../../cta/cta.vue';
import { DESKTOP_MIN_RESOLUTION } from '../../../_imports/utils/global.common.js';
import ThumbnailVideoLoop from '../../../resources/resource-banner/_imports/video-loop.common.js';
import openWistiaChannel from './resource-wjs-wistia-channel';
import animatedArrow from './resource-banner-animated-arrow';
import { getFormattedActionsHistory, getResourcesFromPlatform } from '../../../coveo-platform/_imports/coveo-platform.common';
import Glide from '@glidejs/glide';
import throttle from 'lodash.throttle';
import ResourceVideoPopUp from '../../resource-video-pop-up/_imports/resource-video-pop-up.vue';
import Modal from '../../../modal/modal.vue';

const offset = 154;

const resourceBannerSliderOptions = {
    perView: 4,
    peek : {
        before: offset,
        after: (offset + 60),
    },
    gap: 32,
    bound: true,
    rewind: false,
    breakpoints: {
        1024: {
            gap: 24,
            perView: 2,
            peek : {
                before: 0,
                after: (offset + 60),
            }
        },
        770: {
            gap: 16,
            perView: 1,
            peek : {
                before: 0,
                after: 120,
            },
            bound: true,
        },
        385: {
            gap: 8,
            perView: 1,
            peek : {
                before: 0,
                after: 60,
            },
            bound: true,
        }
    }
};

const resourceBanner = {
    template: getVueTemplate('resource-banner'),
    comments: true,
    mixins: [
        BaseVueComponent,
        decodeHtml,
        DynamicResourceComponent,
        ThumbnailVideoLoop
    ],
    components: {
        CallToAction,
        ResourceCard,
        ResourceVideoPopUp,
        Modal
    },
    props: {
        resourceBanner: Object,
        headings: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            amDisplayAttribute: '',
            resources: [],
            id: null,
            simpleId: null,
            startIndex: 0,
            minimumResources: 6,
            resourcesPerPage: 0,
            previousPage: -1,
            availableTags: [],
            tags: [],
            tagsComputedFieldSource: null,
            previousTags: [],
            resourceTypes: [],
            loadingResources: false,
            gettingResources: false,
            noResults: false,
            hasMoreResources: false,
            resourceFilterBannerSimpleId: null,
            firstRailTranslation: 0,
            firstRailTranslationSpeed: 0.5,
            firstRailHover: false,
            secondRailTranslation: -32,
            secondRailTranslationSpeed: 0.75,
            secondRailHover: false,
            isNavHidden: false,
            viewportIs440orLess: window.innerWidth < 441,

            resourceIdForVideoPopUpModal: null,
            selectedResourceForVideoPopUpModal: null,
            resourcesAvailableForVideoPopUpModal: [],
            selectedResourceIndexForVideoPopUpModal: 0,
            resourceVideoModalOpenedByAnotherInstance: false,
        };
    },
    watch: {
        resources: {
            handler() {
                    if (this.shouldInitSlider()){
                        this.$nextTick(() => {
                            this.initCarousel();

                            this.onResizeThrottled = throttle(this.onResize, 100);

                            window.addEventListener('resize', debouncer(() => {
                                this.onResizeThrottled();
                            }, 250));
                        });
                    } 
                }
            },
        immediate: true
    },
    computed: {
        formProps() {
            let overrideMarketoDetailIDUrl = '';

            if (this.selectedResourceForVideoPopUpModal) {
                overrideMarketoDetailIDUrl = getResourceUrlForMarketoDetailIdUrl(this.selectedResourceForVideoPopUpModal);
            }

            return {
                form: this.data.form,
                overrideThankYouMessage: this.data.overrideThankYouMessage,
                overrideFormTitle: this.data.overrideFormTitle,
                overrideMarketoDetailIDUrl,
            }
        },
        firstRailResources() {
            const resources = this.resources.length > 8 ? [...this.resources.slice(0, this.resources.length / 2)] : this.resources;

            if (resources.length > 0 && resources.length <= 4) {
                resources.forEach(resource => resources.push(resource));
            }

            return resources;
        },
        secondRailResources() {
            const resources = this.resources.length > 8 ? [...this.resources.slice(this.resources.length / 2, this.resources.length)] : []

            if (resources.length > 0 && resources.length <= 4) {
                resources.forEach(resource => resources.push(resource));
            }

            return resources;
        },
        getCardBackgroundColor() {
            return this.data.styling.cardColor ? this.data.styling.cardColor.replace("#", '') : '';
        },
        popUpBehaviourActivated() {
            return this.data.popUpBehaviourActivated;
        },
        numberOfresourcesAvailableForVideoPopUpModal() {
            return this.resourcesAvailableForVideoPopUpModal.length;
        },
        displayPagination() {
            return this.numberOfresourcesAvailableForVideoPopUpModal > 1;
        },
        displayPreviousArrow() {
            return this.selectedResourceIndexForVideoPopUpModal > 0;
        },
        previousResourceThumbnailUrl() {
            if(this.displayPreviousArrow) {
                return this.resourcesAvailableForVideoPopUpModal[this.selectedResourceIndexForVideoPopUpModal - 1]?.thumbnailSrc;
            }

            return null;
        },
        displayNextArrow() {
            return this.selectedResourceIndexForVideoPopUpModal < this.resourcesAvailableForVideoPopUpModal.length - 1;
        },
        nextResourceThumbnailUrl() {
            if(this.displayNextArrow) {
                return this.resourcesAvailableForVideoPopUpModal[this.selectedResourceIndexForVideoPopUpModal + 1]?.thumbnailSrc;
            }

            return null;
        },
        gridManagerDefaultCardWidth() {
            return this.data?.styling?.gridManager?.defaultCardWidth ?? '25%';
        },
        hasGridManager() {
            return !!this.data?.styling?.gridManager;
        },
        gridManagerStyles() {
            return this.hasGridManager ? `--default-card-width: ${this.gridManagerDefaultCardWidth}` : '';
        },
        gridManagerRowWidths() {
            const rowOverrides = this.data.styling.gridManager.rowOverrides;

            if (!rowOverrides || rowOverrides.length === 0) {
                return {};
            }

            return rowOverrides.reduce((map, override) => {
                map[override.rowIndex] = parseInt(override.cardWidthForRow) || 25;
                return map;
            }, {});
        },
        gridManagerRowOverride() { 
            if (this.resources.length === 0) {
                return [];
            };

            if (!this.data?.styling?.gridManager?.rowOverrides?.length) {
                return [];
            };

            const defaultCardWidth = parseInt(this.gridManagerDefaultCardWidth) || 25;
            const rowWidths = this.gridManagerRowWidths;

            const overrides = [];
            let cardIndex = 0;
            let rowIndex = 1;

            while (cardIndex < this.resources.length && rowIndex <= this.resources.length) {
                const rowWidth = rowWidths[rowIndex] || defaultCardWidth;
                const cardsPerRow = Math.floor(100 / rowWidth);
                const remainingCards = this.resources.length - cardIndex;
                const cardsInThisRow = Math.min(cardsPerRow, remainingCards);

                if (rowWidths[rowIndex]) {
                    const widthClass = `${Math.round(rowWidth)}%`;
                    for (let i = 0; i < cardsInThisRow; i++) {
                        overrides.push({
                            index: cardIndex + i,
                            width: widthClass
                        });
                    }
                }
                
                cardIndex += cardsInThisRow;
                rowIndex++;
            }

            return overrides.sort((a, b) => a.index - b.index);
        },
    },
    created() {
        if (this.resourceBanner) {
            this.data = this.resourceBanner;
        }

        if (this.data && this.data.resourcesData && this.data.resourcesData.linkedResourceFilterBannerId) {
            this.resourceFilterBannerSimpleId = this.data.resourcesData.linkedResourceFilterBannerId.toLowerCase()
        }

        this.setAmDisplayAttribute();

        window.addEventListener('resource-video-modal-opened', () => {
            this.resourceVideoModalOpenedByAnotherInstance = true;
        });
    },
    mounted() {
        this.$nextTick(() => {
            let element = this.$el;

            if (!this.$el.hasAttribute('am-resource-banner')) {
                const childElement = this.$el.querySelector(':scope > [am-resource-banner]');

                if (childElement) {
                    element = childElement;
                }
            }
            
            if (element.hasAttribute('am-resource-banner')) {
                element.setAttribute('am-resource-banner', 'loaded');
            }

            this.initAttributes();

            const currentTags = this.getHashFilters();

            if (Array.isArray(currentTags)) {
                this.tags = currentTags;
            }

            if (this.resourceFilterBannerSimpleId) {
                this.listenToFilterChanges();
            } else {
                this.updateResources();
            }

            if(hasDataPageAttribute('wsj-gen-ai')) {
                openWistiaChannel();
                animatedArrow(this.$el);
            }
            
            if(this.data.popUpBehaviourActivated) {
                this.fetchVideoIdFromUrlHash();
            }
        });
    },
    destroyed() {
        window.removeEventListener('resize', this.onResizeThrottle);
    },
    updated() {
        this.newResourcesAreLoaded();
    },
    methods: {
        overrideCardWidthDataForIndex(index) {
            return this.gridManagerRowOverride.find(item => item.index === index)?.width ?? null;
        },
        fetchVideoIdFromUrlHash() {
            if (this.videoPopUpModalOpenedByAnotherInstance) {
                return;
            }

            const params = new URLSearchParams(window.location.hash.slice(1));
            const videoId = params.get('videoId');
            
            if (videoId) {
                this.$watch('resources', (newResources) => {
                    if (newResources.length && !this.resourceVideoModalOpenedByAnotherInstance) {
                        const resourceWithVideo = newResources.find(resource => 
                            this.getResourceVideoId(resource) === videoId
                        );
                        
                        if (resourceWithVideo) {
                            window.dispatchEvent(new CustomEvent('resource-video-modal-opened'));
                            this.openPopUpModal(resourceWithVideo.itemID);
                        }
                    }
                }, { immediate: true });
            }
        },
        initCarousel(){
            this.isResourceBannerSlider = this.$refs.carousel.classList.contains('resources');

            if (this.isResourceBannerSlider) {
                this.carousel = new Glide(this.$refs.carousel, {...resourceBannerSliderOptions} );

                this.alignLastCardToRight();
            }
            
            if (this.carousel) {
                this.carousel.mount();
                this.updateNavVisibility();
                this.hoverVideo();
                this.setVideoSrc();
            } 
        },
        alignLastCardToRight() {
            if(this.viewportIs440orLess && this.carousel) {
                this.carousel.on('move', (e) => {
                    setTimeout( () => {
                        if(!this.carousel.selector.className.includes('glide--dragging')) {
                            this.carousel.selector.querySelector('.glide__slides').style.transform = `translate3d(-${e?.movement}px, 0px, 0px)`;
                        }
                    }, 0);
                });
            }
        },
        updateNavVisibility() {
            this.numberOfSlidesPerView = this.carousel._o.perView;
            this.numberOfSlidesTotal = this.carousel._c.Sizes.length;

            this.isNavHidden = !this.carousel
                || this.carousel && this.numberOfSlidesPerView >= this.numberOfSlidesTotal;

            if (this.isNavHidden) {
                this.carousel.disable();
            } else {
                this.carousel.enable();
            }
        },

        onResize() {
            this.isDesktop = window.innerWidth >= DESKTOP_MIN_RESOLUTION;
            this.viewportIs440orLess = window.innerWidth < 441;

            if (this.isResourceBannerSlider) {
                this.carousel.destroy();
                this.carousel = null;
                if (!this.carousel) {
                    this.initCarousel();
                }
            } 
        },
        shouldInitSlider(){
            if (this.data.styling.isSlider && (this.data.styling.resourcePresentation === 'medium-cards' || this.data.styling.resourcePresentation === 'medium-cards-simple-video')) {
                return true;
            }
        },
        setAmDisplayAttribute() {
            const displayAttribute = [];

            if (this.data.styling.displayTypeIcons) {
                displayAttribute.push('type-icons');
            }

            if (this.data.styling.displayTimeInfo) {
                displayAttribute.push('time-info');
            }

            if (this.data.styling.displayShortDescriptions) {
                displayAttribute.push('short-descriptions')
            }

            if (this.data.styling.displayThumbnails) {
                displayAttribute.push('thumbnails');
            }

            if (this.data.styling.displayResourcesTags) {
                displayAttribute.push('resources-tags');
            }

            if (this.data.styling.displayAnnotations) {
                displayAttribute.push('annotations');
            }

            if (this.data.styling.displayInlineTagging) {
                displayAttribute.push('inline-tagging');
            }

            this.amDisplayAttribute = displayAttribute.join(' ');
        },
        initAttributes() {
            if (this.data.simpleId) {
                this.simpleId = this.data.simpleId.toLowerCase();
            }

            if (this.data.resourcesData.resourcesPerPage) {
                this.resourcesPerPage = this.data.resourcesData.resourcesPerPage;
            }

            if (this.data.resourcesData.maxResources) {
                this.minimumResources = this.data.resourcesData.maxResources;
            }
        },
       
        initThumbnailsOnRails() {
            const cloneHeadToTail = (railElement, railResources) => {
                if (!railElement) {
                    return;
                }

                const headElement = railElement.childNodes[railElement.childNodes.length - railResources.length];

                if (headElement?.getBoundingClientRect().left < 0) {
                    railElement.appendChild(headElement.cloneNode({ deep: true }));
                }
            };

            const animate = () => {
                window.requestAnimationFrame(() => {
                    this.firstRailTranslation -= this.firstRailTranslationSpeed * (this.firstRailHover ? 0.50 : 1);
                    this.secondRailTranslation -= this.secondRailTranslationSpeed * (this.secondRailHover ? 0.50 : 1);

                    cloneHeadToTail(this.$refs.firstRail, this.firstRailResources);

                    if (this.$refs.secondRail) {
                        cloneHeadToTail(this.$refs.secondRail, this.secondRailResources);
                    }

                    animate();
                });
            };

            animate();
        },
        listenToFilterChanges() {
            const hashChange = () => {
                this.updateResources();
            };

            window.addEventListener('hashchange', hashChange);

            hashChange();
        },
        newResourcesAreLoaded() {
            const hiddenElements = this.$el.querySelectorAll('.vue-hidden-until-mounted');

            if (hiddenElements
                && hiddenElements.length) {
                hiddenElements.forEach(hiddenElement => hiddenElement.classList.remove('vue-hidden-until-mounted'));
            }
        },
        updateResources() {
            if (this.data.resourcesData
                && this.data.resourcesData.resourceBannerSelectors) {

                const updateOptions = this.getResourceUpdateOptions();
                this.tags = updateOptions.currentTags;

                if (this.previousPage > updateOptions.currentPage
                    || !isEqual(this.tags, this.previousTags)) {
                    this.resources = [];
                }

                this.previousTags = this.tags;
                this.previousPage = updateOptions.currentPage || 0;

                this.data.resourcesData.resourceBannerSelectors.forEach(resourceBannerSelector => {
                    if (resourceBannerSelector) {
                        if (!this.resourceFilterBannerSimpleId) {
                            if (this.isValidStaticResources(resourceBannerSelector)) {
                                resourceBannerSelector.resources.forEach(resource => {
                                    this.addResourceIfValid(resource);
                                });
                            } else if (this.isValidCoveoPlatformResources(resourceBannerSelector)) {
                                this.addResourcesFromPlatform({
                                    resourceBannerSelector
                                });
                            } else if (this.isValidCoveoPlatformRecommendations(resourceBannerSelector)) {
                                this.addResourcesFromRecommendations({
                                    resourceBannerSelector
                                });
                            } else if (this.isValidCoveoPlatformContextualRecommendations(resourceBannerSelector)) {
                                this.addResourcesFromContextualRecommendations({
                                    resourceBannerSelector
                                });
                            } else if (this.isValidBlogPosts(resourceBannerSelector)) {
                                this.addResourcesFromBlogPosts({
                                    resourceBannerSelector
                                });
                            }
                        } else {
                            let startIndex = 0;
                            let endIndex = this.minimumResources;

                            if (updateOptions.update) {
                                if (updateOptions.currentPage > 1) {
                                    startIndex = this.minimumResources + ((updateOptions.currentPage - 2) * this.resourcesPerPage);
                                    endIndex = startIndex + this.resourcesPerPage;
                                }

                                if (!this.gettingResources) {
                                    this.loadingResources = true;
                                }

                                if (this.isValidStaticResources(resourceBannerSelector)) {
                                    resourceBannerSelector.resources.forEach(resource => {
                                        if (resource.tags
                                            && this.tags.some(tag => resource.tags.includes(decodeURIComponent(tag)))) {
                                            this.addResourceIfValid(resource);
                                        } else if (this.tags && !this.tags.length) {
                                            this.addResourceIfValid(resource);
                                        }
                                    });
                                } else if (this.isValidCoveoPlatformResources(resourceBannerSelector)) {
                                    this.addResourcesFromPlatform({
                                        resourceBannerSelector,
                                        endIndex
                                    });
                                }
                            }
                        }
                    }
                });

                if (this.data.styling.resourcePresentation === 'thumbnails-on-rails') {
                    setTimeout(() => {
                        this.initThumbnailsOnRails();
                    }, 0);
                }
            }
        },
        isValidStaticResources(resourceBannerSelector) {
            return resourceBannerSelector.type === 'static' && resourceBannerSelector.resources && resourceBannerSelector.resources.length;
        },
        isValidCoveoPlatformResources(resourceBannerSelector) {
            return (resourceBannerSelector.type === 'coveo-platform' && resourceBannerSelector.resourceTypes && resourceBannerSelector.resourceTypes.length)
                || resourceBannerSelector.type === 'coveo-platform';
        },
        isValidCoveoPlatformRecommendations(resourceBannerSelector) {
            return resourceBannerSelector.type === 'coveo-recommendations';
        },
        isValidCoveoPlatformContextualRecommendations(resourceBannerSelector) {
            return resourceBannerSelector.type === 'coveo-contextual-recommendations';
        },
        isValidBlogPosts(resourceBannerSelector) {
            return resourceBannerSelector.type === 'blog-posts';
        },
        getResourceUpdateOptions() {
            const currentTags = this.getHashFilters();
            let update = false;
            const currentPage = parseInt(this.getHashResourcePage(this.simpleId)) || 0;

            if (this.previousPage === -1) {
                update = true;
            } else if (!isEqual(currentTags, this.tags)) {
                if (currentPage > 1) {
                    this.updateHashKey({
                        key: `resource-${this.simpleId}-page`,
                        newValue: 1
                    });
                } else {
                    update = true;
                }
            }  else if (this.previousPage === -1
                && (currentPage > 1
                    || !isEqual(this.previousTags, this.tags))) {
                update = true;
            } else if (this.previousPage !== -1
                && currentPage !== this.previousPage) {
                update = true;
            }

            this.tags = currentTags;

            return { 
                update,
                currentTags,
                currentPage
            };
        },
        addResourceIfValid(newResource) {
            const resourceAlreadyExists = this.resources.some(resource => resource.id.toLowerCase() === newResource.id.toLowerCase());
            const resourceAlreadyExistsInVideoPopUpModal = this.resourcesAvailableForVideoPopUpModal.some(resource => resource.id.toLowerCase() === newResource.id.toLowerCase());

            if(newResource.video?.videoCode) {
                this.resourcesAvailableForVideoPopUpModal.push(newResource);
            }

            if (resourceAlreadyExists) {
                return false;
            }

            const resourcePage = document.querySelector('[am-resource-page]');

            if (resourcePage
                && resourcePage.id
                && this.getFormattedSimpleId(resourcePage.id) === newResource.id.toLowerCase()) {
                return;
            }

            let currentPage = this.previousPage - 1;

            if (currentPage < 1) {
                currentPage = 0;
            }

            const maxResources = this.data.resourcesData.maxResources + this.resourcesPerPage * currentPage;

            if (this.resources.length < maxResources) {
                this.resources.push(newResource);
            }

            return true;
        },
        addResourcesFromRecommendations(args = {}) {
            if (!args.resourceBannerSelector) {
                return;
            }

            if (this.isResourcesFull()) {
                return;
            }

            const additionalParameters = [
                `actionsHistory=${getFormattedActionsHistory()}`,
                `pipeline=Resource Center Recommendations`
            ];

            this.addResourcesFromPlatformResponse({
                resourceBannerSelector: args.resourceBannerSelector,
                additionalParameters,
                onSuccess: () => CoveoWebsite.recommendations.push(...this.resources)
            });
        },
        addResourcesFromContextualRecommendations(args = {}) {
            const payload = {
                resourceBannerSelector: args.resourceBannerSelector,
                onSuccess: () => CoveoWebsite.recommendations.push(...this.resources)
            };

            if (typeof CoveoWebsite?.coveoPlatform?.context !== 'undefined'
                && Object.entries(CoveoWebsite.coveoPlatform.context)) {
                payload.context = CoveoWebsite.coveoPlatform.context;
            }

            this.addResourcesFromPlatform(payload);
        },
        addResourcesFromPlatform(args = {}) {
            if (!args.resourceBannerSelector) {
                return;
            }

            if (this.isResourcesFull()) {
                return;
            }

            const resourceBannerSelector = args.resourceBannerSelector;
            const resourceTypes = resourceBannerSelector.resourceTypes;
            const coveoPlatform = resourceBannerSelector.coveoPlatform;
            const tagsComputedFieldSource = resourceBannerSelector.tagsComputedFieldSource;
            const tags = `"${this.tags.join('","')}"`;
            
            const query = {
                firstResult: 0,
                numberOfResults: args.endIndex,
                sortCriteria: resourceBannerSelector.sortCriteria || 'date descending'
            };

            if (args.endIndex) {
                query.numberOfResults = args.endIndex;
            }

            const aqConditions = [];

            if (this.tags
                && this.tags.length
                && tagsComputedFieldSource) {
                aqConditions.push(`@${tagsComputedFieldSource}==(${decodeURIComponent(tags)})`);
            }

            if (resourceTypes
                && resourceTypes.length) {
                const resourceTypesForQuery = `"${resourceTypes.join('","')}"`;

                aqConditions.push(`@resourcetypename==(${resourceTypesForQuery})`)
            }

            if (CoveoWebsite
                && CoveoWebsite.coveoPlatform
                && CoveoWebsite.coveoPlatform.source) {
                aqConditions.push(`@source==("${CoveoWebsite.coveoPlatform.source}")`)
            }

            if (coveoPlatform.aq) {
                aqConditions.push(this.replaceAtChars(coveoPlatform.aq));
            }

            if (resourceBannerSelector.aq) {
                aqConditions.push(this.replaceAtChars(resourceBannerSelector.aq));
            }

            aqConditions.push(`@culture=='${lang}'`);

            query.aq = aqConditions.join(' AND ');

            const payload = {
                resourceBannerSelector,
                query
            };

            if ('onSuccess' in args) {
                payload.onSuccess = args.onSuccess;
            }

            if ('context' in args) {
                payload.context = args.context;
            }

            this.addResourcesFromPlatformResponse(payload);
        },
        addResourcesFromPlatformResponse(args = {}) {
            const payload = {
                ...args,
                coveoPlatform: args.resourceBannerSelector?.coveoPlatform
            };

            getResourcesFromPlatform(payload)
                .then(({ resources, totalCount }) => {
                    let maxResources = args.resourceBannerSelector?.maxResources || -1;

                    resources.forEach(resource => {
                        if (!this.isResourcesFull()
                            && (maxResources <= -1 || maxResources > 0)
                            && this.addResourceIfValid(resource)) {
                            maxResources--;
                        }
                    });

                    this.loadingResources = false;
                    this.gettingResources = false;

                    this.hasMoreResources = this.resources.length < totalCount;

                    if ('onSuccess' in args) {
                        args.onSuccess();
                    }
                });
        },
        addResourcesFromBlogPosts(args = {}) {
            if (!args.resourceBannerSelector) {
                return;
            }

            if (this.isResourcesFull()) {
                return;
            }

            this.loadingResources = true;
            
            const resourceBannerSelector = args.resourceBannerSelector;
            const categories = resourceBannerSelector.categories;
            const perPage = resourceBannerSelector.maxResources || 3;
            const lang = document.querySelector('html').lang || 'en';

            const parameters = [
                `per_page=${perPage}`,
                `lang=${lang}`
            ];

            if (categories?.length) {
                parameters.push(`categories=${categories.join(',')}`);
            }

            http.get(`/api/sitecore/blog/posts?${parameters.join('&')}`)
                .then(response => {
                    if (!response?.data?.posts) {
                        return;
                    }

                    const posts = response.data.posts;

                    let maxResources = -1;

                    if (resourceBannerSelector.maxResources) {
                        maxResources = resourceBannerSelector.maxResources;
                    }

                    posts?.forEach(post => {
                        if (this.isResourcesFull()
                            || maxResources <= 0) {
                            return;
                        }

                        const postLang = lang === 'en' ? '' : `${lang}/`;
                        const url = `https://www.coveo.com/blog/${postLang}${post.slug}/`;

                        let category = post.categories?.[0]?.name;

                        if (categories?.length) {
                            category = post.categories.find(c => categories.includes(c.id))?.name;
                        }
                        
                        const newResource = {
                            id: post.id?.toString(),
                            title: post.title,
                            shortDescription: post.description,
                            externalUrl: url,
                            landingUrl: url,
                            type: {
                                label: category,
                                singularLabel: category,
                                linkLabel: resourceBannerSelector.linkLabel
                            },
                            thumbnail: {
                                src: post.featuredImage
                            }
                        };

                        if (this.addResourceIfValid(newResource)) {
                            maxResources--;
                        }
                    });

                    this.loadingResources = false;
                });
        },
        isResourcesFull() {
            if (this.resourcesPerPage) {
                return false;
            }
            
            return this.resources.length >= this.data.resourcesData.maxResources;
        },
        hasSeeMoreButton() {
            return this.data.resourcesData.seeMoreButton && this.data.resourcesData.seeMoreButton.text && this.hasMoreResources;
        },
        seeMore() {
            if (this.$refs.seeMoreButton) {
                this.gettingResources = true;

                let resourcesPage = parseInt(this.getHashResourcePage(this.simpleId)) || 1;

                if (resourcesPage < 1) {
                    resourcesPage = 1;
                }

                this.updateHashKey({
                    key: `resource-${this.simpleId}-page`,
                    newValue: ++resourcesPage
                });
            }
        },
        escapeRegExp(string) {
            return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        },
        replaceAll(str, find, replace) {
            return str.replace(new RegExp(this.escapeRegExp(find), 'g'), replace);
        },
        getFormattedSimpleId(id) {
            return id.replace('-', '').replace('{', '').replace('}').toLowerCase();
        },
        replaceAtChars(data) {
            return this.replaceAll(data, '@@', '@');
        },
        updateUrlHash(videoId = null) {
            const url = new URL(window.location.href);

            url.searchParams.delete('chapter');

            const hashParams = new URLSearchParams(url.hash.slice(1));

            if (videoId) {
                hashParams.set('videoId', videoId);
            } else {
                hashParams.delete('videoId');
            }

            const newHash = hashParams.toString();
            url.hash = newHash ? `#${newHash}` : '';

            const newUrl = url.pathname + url.search + url.hash;

            history.replaceState(history.state, '', newUrl);
        },
        getResourceVideoId(resource) {	
            return resource?.videoId || resource?.video?.videoCode || null;
        },
        openPopUpModal(resourceId) {
            if(resourceId){
                this.resourceIdForVideoPopUpModal = resourceId;

                this.selectedResourceIndexForVideoPopUpModal = this.resourcesAvailableForVideoPopUpModal.findIndex(resource => resource.itemID === resourceId);
                this.selectedResourceForVideoPopUpModal = this.resourcesAvailableForVideoPopUpModal[this.selectedResourceIndexForVideoPopUpModal];

                if(this.selectedResourceForVideoPopUpModal) {
                    document.documentElement.classList.add('resource-video-pop-up-open');
                    
                    this.$nextTick(() => {
                        if(this.$refs.resourceVideoPopUpModal){
                            this.$refs.resourceVideoPopUpModal.open();
                            this.setResourceIdInUrl();
                        }
                    });
                }
            }
        },
        setResourceIdInUrl() {
            if(this.selectedResourceForVideoPopUpModal) {
                const videoId = this.getResourceVideoId(this.selectedResourceForVideoPopUpModal);
                if(videoId){
                    this.updateUrlHash(videoId);
                }
            }
        },
        cleanupGatingModals() {
           const resourceVideoPopUpModalChildren = this.$refs.resourceVideoPopUpModal?.$children;
           
            if (resourceVideoPopUpModalChildren) {
                resourceVideoPopUpModalChildren.forEach(child => {
                    if (typeof child.cleanupGatingFormModal === 'function') {
                        child.cleanupGatingFormModal();
                    }
                });
            }
        },
        switchToResource(newIndex) {
            this.cleanupGatingModals();
            this.selectedResourceIndexForVideoPopUpModal = newIndex;
            this.selectedResourceForVideoPopUpModal = this.resourcesAvailableForVideoPopUpModal[newIndex];
            this.setResourceIdInUrl();
        },
        openNextResourceInVideoPopUpModal() {
            const nextIndex = this.selectedResourceIndexForVideoPopUpModal + 1;
            if (nextIndex < this.resourcesAvailableForVideoPopUpModal.length) {
                this.switchToResource(nextIndex);
            }
        },
        openPreviousResourceInVideoPopUpModal() {
            const prevIndex = this.selectedResourceIndexForVideoPopUpModal - 1;
            if (prevIndex >= 0) {
                this.switchToResource(prevIndex);
            }
        },
        closeModal() {
            if(this.$refs.resourceVideoPopUpModal){
                this.$refs.resourceVideoPopUpModal.close();
                document.documentElement.classList.remove('resource-video-pop-up-open');
                this.updateUrlHash();
            }
        },
        destroyModal() {
            setTimeout(() => {
                if(this.$refs.resourceVideoPopUpModal?.$el){
                    this.$refs.resourceVideoPopUpModal.$el.remove();
                }
                
                this.resourceIdForVideoPopUpModal = null;
                this.selectedResourceForVideoPopUpModal = null;
            }, 250);
        },
        triggerPagination(direction) {
            if(direction === 'next') {
                this.openNextResourceInVideoPopUpModal();
            } else {
                this.openPreviousResourceInVideoPopUpModal();
            }
        }
    },
};

export default resourceBanner;